/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Application from 'flarum/common/Application';
import CountryRedirectModal, { getRedirectTarget } from '../components/CountryRedirectModal';
import { onConsentReady } from '../utils/consentGate';
import { isRedirectSuppressed } from '../utils/redirectCookie';

/**
 * Payload of the `/guest-country` endpoint, resolved offline from the DB-IP
 * databases bundled with the base image.
 */
export interface GuestCountryResponse {
	countryCode?: string | null;
}

/**
 * `Application.mount()` is `protected`, so it is invisible to `extend()`'s key
 * lookup. Widening the prototype to this shape keeps the patch typed.
 */
export type MountableApplication = {
	mount(basePath?: string): void;
};

function fetchGuestCountry(): Promise<string | null> {
	return app
		.request<GuestCountryResponse>({ method: 'GET', url: `${app.forum.attribute<string>('apiUrl')}/guest-country` })
		.then((data) => data?.countryCode || null)
		.catch(() => null);
}

export default function () {
	extend(Application.prototype as unknown as MountableApplication, 'mount', () => {
		// Only for guests
		if (app.session.user) return;

		if (isRedirectSuppressed()) return;

		// Start fetching country in parallel with the UC consent banner
		const countryPromise = fetchGuestCountry();

		onConsentReady(() => {
			countryPromise.then((countryCode) => {
				const target = getRedirectTarget(countryCode);

				if (!target) return;

				app.modal.show(CountryRedirectModal, { target });
			});
		});
	});
}
